import React, { useState, useEffect, useMemo, useRef } from 'react';
import Map, { NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import LocationMarker from './LocationMarker';
import EntryPopup from './EntryPopup';
import { geocodeLocation } from '../../services/geocodingService';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

const MapContainer = ({ entries = [] }) => {
  const [viewState, setViewState] = useState({
    longitude: -74.0060,
    latitude: 40.7128,
    zoom: 1
  });

  // State for storing geocoded locations: { "KeywordName": { longitude: ..., latitude: ... } }
  const [locations, setLocations] = useState({});
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Group entries by keyword and filter noise
  const groupedEntries = useMemo(() => {
    if (!entries || entries.length === 0) return {};
    
    // 1. Group by keyword
    const byKeyword = entries.reduce((acc, entry) => {
      const keyword = entry.keyword?.trim();
      if (keyword) {
        if (!acc[keyword]) {
          acc[keyword] = [];
        }
        acc[keyword].push(entry);
      }
      return acc;
    }, {});

    // 2. Filter noise and determine location for each keyword
    const validGroups = {};
    
    Object.entries(byKeyword).forEach(([keyword, groupEntries]) => {
      // Count location frequency
      const locationCounts = {};
      groupEntries.forEach(entry => {
        const loc = entry.location?.trim();
        if (loc) {
          locationCounts[loc] = (locationCounts[loc] || 0) + 1;
        }
      });

      // Find most frequent location
      let maxCount = 0;
      let bestLocation = null;
      
      Object.entries(locationCounts).forEach(([loc, count]) => {
        if (count > maxCount) {
          maxCount = count;
          bestLocation = loc;
        }
      });

      // Keep the location that appears the most (no threshold)
      if (bestLocation) {
        validGroups[keyword] = {
          entries: groupEntries,
          locationToGeocode: bestLocation
        };
      }
    });

    return validGroups;
  }, [entries]);

  useEffect(() => {
    const fetchCoordinates = async () => {
      setLoading(true);
      
      const keywords = Object.keys(groupedEntries);
      const newLocations = { ...locations };
      let hasNewLocations = false;

      // Filter out keywords that are already geocoded
      const keywordsToGeocode = keywords.filter(kw => !newLocations[kw]);

      if (keywordsToGeocode.length === 0) {
        setLoading(false);
        return;
      }

      // Process in batches
      for (const keyword of keywordsToGeocode) {
        const locationString = groupedEntries[keyword].locationToGeocode;
        
        try {
          // Add a small delay between requests
          await new Promise(resolve => setTimeout(resolve, 200));
          
          const coords = await geocodeLocation(locationString);
          if (coords) {
            newLocations[keyword] = coords;
            hasNewLocations = true;
          }
        } catch (error) {
          console.error(`Failed to geocode ${locationString} for keyword ${keyword}`, error);
        }
      }

      if (hasNewLocations && isMountedRef.current) {
        setLocations(newLocations);
      }
      if (isMountedRef.current) {
        setLoading(false);
      }
    };

    if (Object.keys(groupedEntries).length > 0) {
      fetchCoordinates();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedEntries]);

  const handleMarkerClick = (keyword) => {
    setSelectedLocation(keyword);
  };

  const handlePopupClose = () => {
    setSelectedLocation(null);
  };

  if (!MAPBOX_TOKEN) {
    return <div style={{ padding: '20px', color: 'red' }}>Error: Mapbox token not found in environment variables.</div>;
  }

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1,
          background: 'rgba(255, 255, 255, 0.9)',
          padding: '10px 20px',
          borderRadius: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}>
          Processing and Geocoding...
        </div>
      )}
      
      <Map
        {...viewState}
        onMove={evt => setViewState(evt.viewState)}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v11"
        mapboxAccessToken={MAPBOX_TOKEN}
      >
        <NavigationControl position="top-right" />

        {Object.entries(locations).map(([keyword, coords]) => (
          <LocationMarker
            key={keyword}
            location={keyword} 
            position={coords}
            onClick={handleMarkerClick}
          />
        ))}

        {selectedLocation && locations[selectedLocation] && (
          <EntryPopup
            location={selectedLocation} // This displays the Keyword as title
            entries={groupedEntries[selectedLocation].entries}
            position={locations[selectedLocation]}
            onClose={handlePopupClose}
          />
        )}
      </Map>
    </div>
  );
};

export default MapContainer;
